import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { recordNavigation, prefetchRoutesIdle } from "@/lib/routePrefetch";

/**
 * Observa mudanças de rota e:
 *  1) registra hit/miss/cold para métricas de prefetch
 *  2) prefetcha rotas adjacentes prováveis a partir da rota atual
 *     (ex.: ao entrar em /discover, prefetch de /matches e /chat)
 *
 * Reduz o TTI nas próximas navegações sem competir com o boot inicial.
 */
const adjacency: Record<string, string[]> = {
  "/discover": ["/matches", "/chat", "/who-liked-you"],
  "/matches": ["/discover", "/chat", "/who-liked-you"],
  "/venues": ["/discover", "/events"],
  "/profile": ["/my-subscription", "/plans"],
  "/who-liked-you": ["/chat", "/matches", "/plans"],
};

// Trio crítico de navegação do usuário autenticado.
// É prefetchado em QUALQUER rota protegida — assim, mesmo entrando
// por /profile, /events ou /venue/:id, a próxima transição para
// Discover/Matches/Chat é instantânea.
const CORE_USER_ROUTES = ["/discover", "/matches", "/chat"];

const PROTECTED_PREFIXES = [
  "/discover",
  "/matches",
  "/chat",
  "/profile",
  "/venues",
  "/events",
  "/venue/",
  "/who-liked-you",
  "/my-subscription",
  "/referral",
  "/meus-dados",
];

const RoutePrefetchTracker = () => {
  const { pathname } = useLocation();
  const lastTrackedRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastTrackedRef.current === pathname) return;
    lastTrackedRef.current = pathname;

    recordNavigation(pathname);

    const base = pathname.split("?")[0].split("#")[0];

    // 1) Prefetch sempre do trio crítico em rotas autenticadas.
    const isProtected = PROTECTED_PREFIXES.some(
      (p) => base === p || base.startsWith(p)
    );
    if (isProtected) prefetchRoutesIdle(CORE_USER_ROUTES);

    // 2) Prefetch das rotas adjacentes específicas da rota atual.
    const root = Object.keys(adjacency).find((k) => base === k || base.startsWith(k + "/"));
    if (root) prefetchRoutesIdle(adjacency[root]);
  }, [pathname]);

  return null;
};

export default RoutePrefetchTracker;
